import FileUpload from "components/FileUpload";
import Loading from "components/Loading";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  addResolution,
  editAppeal2,
  editResolution,
} from "services/complaintDetailService";
import { editComplaintReception } from "services/complaintService";
import { adminOrgStructure } from "services/orgService";
import {
  Appeal,
  ComplaintReception,
  ResolutionDecisionType,
  Resolutions,
  Structure,
} from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandlerOnForm } from "utils/helper";

type Props = {
  isEdit?: boolean;
  resolution?: Resolutions;
  compliant?: ComplaintReception;
  complaintId?: number | string;
  appeal?: Appeal;
  onClose: (refetch: boolean) => void;
};

const ResolutionForm: FC<Props> = ({
  isEdit = false,
  resolution,
  compliant,
  complaintId,
  appeal,
  onClose,
}) => {
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();
  const navigate = useNavigate();
  const [formState, setFormState] = useState<Resolutions>(
    resolution ?? { decision_type: "further_review" },
  );
  const [loading, setLoading] = useState(false);
  const [attachment, setAttachment] = useState<File | null>();
  const [action, setAction] = useState<"On Appointment" | "Resolved">(
    "On Appointment",
  );

  const fetchStructure = async (adminId: string, orgId: string) => {
    const structure = await adminOrgStructure(adminId, orgId);
    setStructure(structure);
  };

  const [structure, setStructure] = useState<Structure>();

  useEffect(() => {
    if (loginData) {
      fetchStructure(
        loginData?.admin_unit.toString(),
        loginData?.org_unit.toString(),
      );
    }
  }, [loginData]);

  const [lowerAdminId, setLowerAdminId] = useState("");
  const [lowerOrgId, setLowerOrgId] = useState("");

  useEffect(() => {
    if (resolution) {
      setFormState(resolution);
      setAction(
        resolution?.decision_type === "further_review"
          ? "On Appointment"
          : "Resolved",
      );
    }
  }, [resolution]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!formState.decision_type && action === "Resolved")
      return "Decision Type is Required.";
    if (!formState.appointment_date && action === "On Appointment")
      return "Appointment Date is Required.";
    if (
      formState.appointment_date &&
      action === "On Appointment" &&
      new Date(formState.appointment_date) < new Date()
    ) {
      return `Appointment Date must be after ${new Date().toLocaleDateString()}`;
    }
    if (
      formState.appointment_date &&
      action === "Resolved" &&
      new Date(formState.appointment_date) > new Date()
    ) {
      return `Resolution Date must be on or before ${new Date().toLocaleDateString()}`;
    }
    return "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }

    setLoading(true);
    const submitAction = isEdit ? editResolution : addResolution;
    const isRegional =
      structure?.admin_unit?.parent_unit === null ? true : false;
    const admin_unit_id =
      formState?.decision_type === "forwarded"
        ? lowerAdminId
        : formState?.decision_type === "forward_to_higher_authority"
          ? loginData?.admin_unit
          : formState?.decision_type === "forward_to_lower_authority"
            ? lowerAdminId
              ? lowerAdminId
              : loginData?.admin_unit
            : loginData?.admin_unit;

    const org_unit_id =
      formState?.decision_type === "forwarded" ||
      (!structure?.org_unit?.parent_unit &&
        !isRegional &&
        formState?.decision_type === "resolved")
        ? null
        : formState?.decision_type === "resolved"
          ? structure?.org_unit?.parent_unit
            ? structure?.org_unit?.parent_unit
            : loginData?.org_unit
          : formState?.decision_type === "forward_to_higher_authority"
            ? structure?.org_unit?.parent_unit
              ? structure?.org_unit?.parent_unit
              : null
            : formState?.decision_type === "forward_to_lower_authority"
              ? lowerOrgId
                ? lowerOrgId
                : loginData?.org_unit
              : loginData?.org_unit;

    submitAction({
      ...formState,
      decision_type:
        formState?.decision_type === "forwarded"
          ? "forward_to_lower_authority"
          : formState?.decision_type,
      complaint: complaintId,
      appealed_complaint_id: appeal?.id,
      appealed_complaint: appeal?.id,
      resolution_document: attachment,
      resolved_by: loginData?.full_name,
      resolved_by_user_id: loginData?.user_id,
      admin_unit_id: loginData?.admin_unit,
      org_unit_id: loginData?.org_unit,
    })
      .then(() => {
        if (compliant) {
          editComplaintReception({
            id: compliant?.id,
            status:
              formState?.decision_type === "further_review"
                ? "on_appointment"
                : formState?.decision_type === "resolved"
                  ? "forwarded_to_registrar"
                  : formState?.decision_type === "close"
                    ? "closed"
                    : "forwarded_to_management",
            admin_unit_id,
            org_unit_id,
            is_transfered:
              formState?.decision_type === "forwarded" ? true : false,
          });
        }
        if (appeal) {
          // const admin_unit_id =
          //   formState?.decision_type === "forwarded"
          //     ? lowerAdminId
          //     : formState?.decision_type === "forward_to_higher_authority"
          //       ? structure?.org_unit?.parent_unit
          //         ? appeal?.admin_unit_id
          //         : structure?.admin_unit?.parent_unit ||
          //           appeal?.admin_unit_id
          //       : appeal?.admin_unit_id;

          // const org_unit_id =
          //   formState?.decision_type === "forwarded" ||
          //   (!structure?.org_unit?.parent_unit &&
          //     !isRegional &&
          //     formState?.decision_type === "resolved")
          //     ? null
          //     : formState?.decision_type === "forward_to_higher_authority"
          //       ? structure?.org_unit?.parent_unit
          //         ? structure?.org_unit?.parent_unit
          //         : null
          //       : appeal?.org_unit_id;

          editAppeal2({
            id: appeal?.id,
            status:
              formState?.decision_type === "further_review"
                ? "on_appointment"
                : formState?.decision_type === "resolved"
                  ? "forwarded_to_registrar"
                  : formState?.decision_type === "close"
                    ? "closed"
                    : "forwarded_to_management",
            complaint: appeal?.complaint,
            parent_appeal: appeal.parent_appeal_id || appeal?.parent_appeal,
            admin_unit_id,
            org_unit_id,
          });
        }

        showTooltip(
          isEdit ? "Successfully Updated!" : "Successfully Submitted!",
          "success",
        );
        setFormState({});
        navigate(appeal ? "/appeal" : "/complaint", { replace: true });
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
      })
      .finally(() => setLoading(false));
  };

  const handleFile = (file: File | null) => {
    setAttachment(file);
  };

  if (!loginData) return <Loading />;

  return (
    <div className="w-full bg-white rounded-xl">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-1">
          {action === "Resolved" ? "Add Decision" : "Set Appointment"}
        </h2>
        <p className="text-sm text-gray-600">
          Fill out the form to{" "}
          {action === "Resolved" ? "add a resolution" : "set an appointment"}{" "}
          for this case.
        </p>
      </div>

      <form className="space-y-6" onSubmit={handleSubmit}>
        <div className="space-y-4">
          {/* Decision Type */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Decision Type *
            </label>
            <select
              required
              name="decision_type"
              value={formState?.decision_type ? formState?.decision_type : ""}
              onChange={(e) => {
                setFormState({
                  ...formState,
                  decision_type: e.target
                    .value as keyof typeof ResolutionDecisionType,
                });
                setAction(
                  e.target.value === "further_review"
                    ? "On Appointment"
                    : "Resolved",
                );
              }}
              className="w-full border border-gray-300 rounded-lg bg-gray-50 p-3 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
            >
              {((appeal &&
                !structure?.org_unit?.parent_unit &&
                structure?.admin_unit?.parent_unit) ||
                structure?.org_unit?.parent_unit) && (
                <option value="forward_to_higher_authority">
                  Forward to Higher Authority
                </option>
              )}
              <option value="resolved">Resolved</option>
              <option value="further_review">Further Review</option>
              {!appeal &&
                (structure?.child_org_units?.tree?.children || []) && (
                  <option value="forward_to_lower_authority">
                    Forward to Lower Authority
                  </option>
                )}
              <option value="forwarded">Transfer</option>
              <option value="close">Close</option>
            </select>
          </div>

          {/* Lower Admin Unit (conditionally shown) */}
          {formState?.decision_type === "forwarded" && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Lower Admin Unit *
              </label>
              <select
                value={lowerAdminId}
                onChange={(e) => {
                  setLowerAdminId(e.target.value);
                }}
                className="w-full border border-gray-300 rounded-lg bg-gray-50 p-3 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
              >
                <option value="">Select Lower Admin Unit</option>
                {(structure?.child_admin_units?.units || []).map((unit) => (
                  <option key={unit?.uu_id} value={unit?.uu_id}>
                    {unit?.unit_name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Lower Org Unit (conditionally shown) */}
          {formState?.decision_type === "forward_to_lower_authority" && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Lower Org Unit *
              </label>
              <select
                value={lowerOrgId}
                onChange={(e) => {
                  setLowerOrgId(e.target.value);
                }}
                className="w-full border border-gray-300 rounded-lg bg-gray-50 p-3 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
              >
                <option value="">Select Lower Org Unit</option>
                {(structure?.child_org_units?.tree?.children || []).map(
                  (unit) => (
                    <option key={unit?.uu_id} value={unit?.uu_id}>
                      {unit?.unit_name}
                    </option>
                  ),
                )}
              </select>
            </div>
          )}

          {/* Date Field */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              {action === "Resolved"
                ? "Resolution Date *"
                : "Appointment Date *"}
            </label>

            <input
              type="date"
              className="w-full border border-gray-300 rounded-lg bg-gray-50 p-3 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
              name="appointment_date"
              value={
                formState.appointment_date
                  ? new Date(formState.appointment_date)
                      .toISOString()
                      .split("T")[0]
                  : ""
              }
              // max={
              //   action === "Resolved"
              //     ? new Date().toISOString().split("T")[0]
              //     : undefined
              // }
              // min={
              //   action === "On Appointment"
              //     ? new Date().toISOString().split("T")[0]
              //     : undefined
              // }
              onChange={(e) =>
                handleChange({
                  ...e,
                  target: { ...e.target, name: "appointment_date" },
                })
              }
            />
          </div>

          {/* Appointment Reason (conditionally shown) */}
          {action === "On Appointment" && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Appointment Reason *
              </label>
              <textarea
                rows={4}
                className="w-full border border-gray-300 rounded-lg bg-gray-50 p-3 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all resize-none"
                name="appointment_reason"
                value={formState?.appointment_reason}
                onChange={handleChange}
                placeholder="Enter the reason for the appointment..."
              />
            </div>
          )}

          {/* Decision Summary (conditionally shown) */}
          {action === "Resolved" && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Decision Summary *
              </label>
              <textarea
                rows={4}
                className="w-full border border-gray-300 rounded-lg bg-gray-50 p-3 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all resize-none"
                name="decision_summary"
                value={formState?.decision_summary}
                onChange={handleChange}
                placeholder="Enter the decision summary..."
              />
            </div>
          )}

          {/* File Upload (conditionally shown) */}
          {action === "Resolved" && (
            <div className="space-y-2">
              <FileUpload
                label="Decision Document"
                fileTypes="*"
                info="Click to upload or drag and drop"
                required={false}
                onChange={handleFile}
              />
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            className="px-6 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-200"
            disabled={loading}
            onClick={(e) => {
              e.preventDefault();
              onClose(false);
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            // className="flex items-center gap-2 px-6 py-2.5 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            className="flex-1 px-6 py-2.5 bg-gradient-to-r from-green-600 to-green-700 text-white font-medium rounded-lg hover:from-green-700 hover:to-green-800 transition-all shadow-md hover:shadow-lg flex items-center justify-center"
            disabled={loading || !structure}
          >
            {loading ? (
              <>
                <span className="animate-spin">{LoadingIcon}</span>
                Processing...
              </>
            ) : action === "Resolved" ? (
              "Submit Resolution"
            ) : (
              "Set Appointment"
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ResolutionForm;
